import {
  BUILDING_TIMEZONE,
  GRID_END_MINUTE,
  GRID_ROW_HEIGHT,
  GRID_START_MINUTE,
  SLOT_MINUTES,
  buildingMinutesOfDay,
  clockTime,
  snapToSlot,
} from '@boud/contracts';

/**
 * Calendar arithmetic for FEAT-006's three views.
 *
 * Kept out of the components because the month, week and day grids all need the
 * same date walking, and the prototype's copies of it drifted — its month grid
 * and its week strip disagreed about which day a week starts on.
 *
 * Every date here is a `YYYY-MM-DD` string in the *building's* timezone, not a
 * `Date`. A `Date` carries a time and a zone, and the whole class of "the
 * booking moved a day" bugs comes from letting those two things travel together
 * where only the calendar day was meant.
 */

/** Today, as the building sees it. */
export function todayInBuilding(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: BUILDING_TIMEZONE });
}

/**
 * Adds days to a `YYYY-MM-DD`.
 *
 * Done by parsing at noon UTC rather than midnight: midnight plus a timezone
 * offset can land on the previous day, and adding 1 to "the last day of the
 * month" then silently returns the same day.
 */
export function addDays(date: string, days: number): string {
  const at = new Date(`${date}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

export function addMonths(date: string, months: number): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const at = new Date(Date.UTC(year, month - 1 + months, 1, 12));
  // Clamp: adding a month to 31 January must not overflow into March.
  const lastDay = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 0, 12)).getUTCDate();
  at.setUTCDate(Math.min(day, lastDay));
  return at.toISOString().slice(0, 10);
}

/** Day of the week, 0 = Sunday — the week the prototype's grid starts on. */
export function weekdayOf(date: string): number {
  return new Date(`${date}T12:00:00Z`).getUTCDay();
}

export function startOfWeek(date: string): string {
  return addDays(date, -weekdayOf(date));
}

export function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

/**
 * The full month grid, including the leading and trailing days of the
 * neighbouring months that the prototype's cells render greyed out.
 *
 * Returned as one flat list because that is what the range query needs: asking
 * for the month alone would leave those visible cells empty.
 */
export function monthGridDays(date: string): string[] {
  const first = startOfMonth(date);
  const gridStart = addDays(first, -weekdayOf(first));
  const daysInMonth = new Date(
    Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)), 0, 12),
  ).getUTCDate();
  const weeks = Math.ceil((weekdayOf(first) + daysInMonth) / 7);

  return Array.from({ length: weeks * 7 }, (_, i) => addDays(gridStart, i));
}

export function weekDays(date: string): string[] {
  const start = startOfWeek(date);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

// ---------------------------------------------------------------------------
// The pixel grid
// ---------------------------------------------------------------------------

export const GRID_HEIGHT = ((GRID_END_MINUTE - GRID_START_MINUTE) / 60) * GRID_ROW_HEIGHT;

export function minutesToY(minutes: number): number {
  return ((minutes - GRID_START_MINUTE) / 60) * GRID_ROW_HEIGHT;
}

/** A pixel offset within a column back to a snapped minute-of-day (REQ-037). */
export function yToMinutes(y: number): number {
  const raw = GRID_START_MINUTE + (y / GRID_ROW_HEIGHT) * 60;
  return Math.min(GRID_END_MINUTE, Math.max(GRID_START_MINUTE, snapToSlot(raw)));
}

export const GRID_HOURS = Array.from(
  { length: (GRID_END_MINUTE - GRID_START_MINUTE) / 60 },
  (_, i) => GRID_START_MINUTE / 60 + i,
);

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** `2 PM` / `٢ م` — the prototype's hour label. */
export function hourLabel(hour: number, locale: string): string {
  const at = new Date(Date.UTC(2024, 0, 1, hour));
  return at.toLocaleTimeString(locale, { hour: 'numeric', hour12: true, timeZone: 'UTC' });
}

/** `2:30 PM`, in the building's timezone. */
export function timeLabel(instant: string | Date, locale: string): string {
  return new Date(instant).toLocaleTimeString(locale, {
    timeZone: BUILDING_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function dateLabel(date: string, locale: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(locale, {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function monthLabel(date: string, locale: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(locale, {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  });
}

export function weekdayLabel(date: string, locale: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(locale, {
    timeZone: 'UTC',
    weekday: 'short',
  });
}

export function dayNumberLabel(date: string, locale: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(locale, {
    timeZone: 'UTC',
    day: 'numeric',
  });
}

/** A Riyadh wall-clock slot as the ISO instant the API expects. */
export function instantFor(date: string, minutes: number): string {
  return `${date}T${clockTime(minutes)}:00+03:00`;
}

export function minutesOfInstant(instant: string | Date): number {
  return buildingMinutesOfDay(new Date(instant));
}

// ---------------------------------------------------------------------------
// Overlap lane packing
// ---------------------------------------------------------------------------

export interface Placed<T> {
  item: T;
  lane: number;
  lanes: number;
}

/**
 * Packs overlapping items into side-by-side lanes.
 *
 * Ported from the prototype's `bkLanes`, with one behavioural fix. That version
 * divided every event by the total number of lanes used across the whole day, so
 * a single 09:00 meeting rendered a quarter-width because three unrelated
 * meetings overlapped at 16:00. Lanes are counted per *cluster* of mutually
 * overlapping items here, which is what a calendar is expected to do.
 */
export function packLanes<T>(
  items: T[],
  startOf: (item: T) => number,
  endOf: (item: T) => number,
): Placed<T>[] {
  const sorted = [...items].sort((a, b) => startOf(a) - startOf(b) || endOf(a) - endOf(b));

  const placed: Placed<T>[] = [];
  let cluster: Placed<T>[] = [];
  let clusterEnd = -Infinity;
  let laneEnds: number[] = [];

  const closeCluster = (): void => {
    const lanes = Math.max(1, laneEnds.length);
    for (const entry of cluster) placed.push({ ...entry, lanes });
    cluster = [];
    laneEnds = [];
  };

  for (const item of sorted) {
    const start = startOf(item);

    // A gap with nothing running means the previous cluster is finished, and the
    // next one starts its lane count from scratch.
    if (start >= clusterEnd && cluster.length > 0) closeCluster();

    let lane = laneEnds.findIndex((end) => end <= start);
    if (lane < 0) {
      laneEnds.push(endOf(item));
      lane = laneEnds.length - 1;
    } else {
      laneEnds[lane] = endOf(item);
    }

    cluster.push({ item, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, endOf(item));
  }

  if (cluster.length > 0) closeCluster();

  return placed;
}

export { SLOT_MINUTES, GRID_ROW_HEIGHT };
